import { DEFAULT_BRIEF_SEQUENCE_VERSION, defaultBriefSequenceForCountry } from "./default-sequence";
import {
  briefAudioMinutesForLength,
  briefDepthForProfileLength,
  briefReadMinutesForLength,
  canPersistReadyMadeBriefProfile,
  migrateBriefProfileForCountryChange,
  newBriefProfileId,
  normalizeBriefProfileCollection,
  type BriefAudioMinutes,
  type BriefDepth,
  type BriefProfile,
  type BriefProfileCollection,
  type BriefProfileLength,
  type BriefProfilePurpose,
  type BriefReadMinutes,
  type BriefStyleId,
  type BriefTone,
} from "./run-store";

export type BriefStyleTemplate = {
  id: Exclude<BriefStyleId, "daily">;
  label: string;
  description: string;
  preferredLength: BriefProfileLength;
  targetMinutes: BriefReadMinutes;
  tone: BriefTone;
  purpose: BriefProfilePurpose;
  narrationEnabled: boolean;
  premium?: boolean;
  sectionIdsForCountry: (country: string) => string[];
};

const PRIMARY_SPORT_BY_COUNTRY: Record<string, string> = {
  US: "nfl",
  CA: "hockey",
  AU: "afl",
  GB: "football",
  DE: "football",
  FR: "football",
  IN: "cricket",
  JP: "baseball",
};

const SPORTS_BY_COUNTRY: Record<string, string[]> = {
  US: ["nfl", "mlb", "nba", "football"],
  CA: ["hockey", "nba", "football"],
  AU: ["afl", "football", "cricket"],
  GB: ["football", "cricket"],
  DE: ["football"],
  FR: ["football"],
  IN: ["cricket", "football"],
  JP: ["baseball", "football"],
};

const NEWS_CATEGORIES_BY_COUNTRY: Record<string, string[]> = {
  US: ["national", "world", "business", "politics", "sports", "technology", "ai"],
  CA: ["national", "world", "business", "politics", "sports", "technology", "ai"],
  AU: ["national", "world", "business", "politics", "sports", "technology", "ai"],
  GB: ["national", "world", "business", "politics", "sports", "technology", "ai"],
  DE: ["national", "world", "business", "sports", "technology", "ai"],
  FR: ["national", "world", "business", "politics", "sports", "technology", "ai"],
  IN: ["national", "world", "business", "politics", "sports", "technology", "ai"],
  JP: ["world", "technology", "ai"],
};

const STATIC_BRIEF_SECTION_IDS = new Set([
  "quote",
  "spark",
  "weather",
  "commute",
  "market_pulse",
  "watchlist",
  "todos",
  "my_day",
  "world_affairs",
  "fun_facts",
  "space",
  "daily_mastery",
  "today_idea",
  "news",
]);
const LEGACY_MARKET_SECTION_IDS = new Set([
  "market",
  "market_indexes",
  "market_commodities",
  "market_energy_crypto",
]);
const CRICKET_SECTION_COUNTRIES = new Set(["AU", "IN"]);
const CUSTOM_WEATHER_PREFIX = "weather_custom_";

function normalizeCountry(country: string): string {
  const normalized = String(country || "US")
    .trim()
    .toUpperCase()
    .slice(0, 2);
  return /^[A-Z]{2}$/.test(normalized) ? normalized : "US";
}

function countryNewsSection(country: string, category: string): string {
  return `news_global_${normalizeCountry(country).toLowerCase()}_${category.trim().toLowerCase()}`;
}

function sportSection(sportId: string): string {
  return `sport_${sportId.trim().toLowerCase()}`;
}

function distinct(values: readonly string[]): string[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    const id = String(value || "")
      .trim()
      .toLowerCase();
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function primarySport(country: string): string {
  const cc = normalizeCountry(country);
  return PRIMARY_SPORT_BY_COUNTRY[cc] || "football";
}

function countrySports(country: string): string[] {
  const cc = normalizeCountry(country);
  return (SPORTS_BY_COUNTRY[cc] || ["football"]).map(sportSection);
}

function knownSectionIdsForCountry(country: string): Set<string> {
  const cc = normalizeCountry(country);
  return new Set([
    ...STATIC_BRIEF_SECTION_IDS,
    ...(CRICKET_SECTION_COUNTRIES.has(cc) ? ["cricket"] : []),
    ...(NEWS_CATEGORIES_BY_COUNTRY[cc] || []).map((category) => countryNewsSection(cc, category)),
    ...countrySports(cc),
  ]);
}

function sanitizedSectionIdsForCountry(
  sectionIds: readonly string[],
  country: string,
  fallbackWhenEmpty: boolean,
): string[] {
  const cc = normalizeCountry(country);
  const knownIds = knownSectionIdsForCountry(cc);
  const expanded = sectionIds.flatMap((value) => {
    const id = String(value || "")
      .trim()
      .toLowerCase();
    return LEGACY_MARKET_SECTION_IDS.has(id) ? ["market_pulse"] : [id];
  });
  const sanitized = distinct(
    expanded.filter(
      (id) => knownIds.has(id) || (id.startsWith(CUSTOM_WEATHER_PREFIX) && id.length > 15),
    ),
  );
  return sanitized.length || !fallbackWhenEmpty ? sanitized : defaultBriefSequenceForCountry(cc);
}

function sameIds(left: readonly string[], right: readonly string[]): boolean {
  return (
    left.length === right.length && left.every((sectionId, index) => sectionId === right[index])
  );
}

function sanitizeBriefProfileForCountry(
  profile: BriefProfile,
  country: string,
  nowEpochMs: number,
): BriefProfile {
  const marketCountry = normalizeCountry(country);
  const sectionIds = sanitizedSectionIdsForCountry(profile.sectionIds, marketCountry, true);
  const disabledSectionIds = sanitizedSectionIdsForCountry(
    profile.disabledSectionIds,
    marketCountry,
    false,
  );
  const lineupChanged =
    !sameIds(sectionIds, profile.sectionIds) ||
    !sameIds(disabledSectionIds, profile.disabledSectionIds);
  return {
    ...profile,
    marketCountry,
    sectionIds,
    disabledSectionIds,
    updatedAtEpochMs: lineupChanged ? nowEpochMs : profile.updatedAtEpochMs,
  };
}

function isUserCustomizedStyle(profile: BriefProfile): boolean {
  return (
    profile.purpose === "custom" ||
    Boolean(profile.templateId.trim()) ||
    profile.name !== "Daily Brief"
  );
}

function quickDailySections(country: string): string[] {
  const cc = normalizeCountry(country);
  const quickSports = [
    sportSection("football"),
    cc === "IN" ? "cricket" : sportSection(primarySport(cc)),
  ];
  return distinct([
    "news",
    countryNewsSection(cc, "world"),
    "market_pulse",
    "watchlist",
    ...quickSports,
    "quote",
  ]);
}

function dailyStarterSections(): string[] {
  return ["news", "my_day", "todos", "weather", "commute", "quote"];
}

function executiveSections(): string[] {
  return ["market_pulse", "watchlist", "news"];
}

function sportsFanSections(country: string): string[] {
  const cc = normalizeCountry(country);
  return distinct([countryNewsSection(cc, "sports"), ...countrySports(cc)]);
}

function learningSections(): string[] {
  return ["fun_facts", "world_affairs", "space", "daily_mastery", "today_idea"];
}

function deepFocusSections(country: string): string[] {
  const cc = normalizeCountry(country);
  const sports = cc === "AU" || cc === "GB" ? countrySports(cc) : [sportSection(primarySport(cc))];
  const extraNews =
    cc === "IN" || cc === "JP"
      ? [countryNewsSection(cc, "technology")]
      : cc === "DE"
        ? [countryNewsSection(cc, "business")]
        : [];
  return distinct([
    "news",
    countryNewsSection(cc, "world"),
    "quote",
    "market_pulse",
    "watchlist",
    ...sports,
    "my_day",
    "todos",
    "fun_facts",
    "world_affairs",
    "space",
    countryNewsSection(cc, "ai"),
    ...extraNews,
    "today_idea",
    "weather",
    "commute",
  ]);
}

export const DEFAULT_BRIEF_STYLE = {
  id: "daily" as const,
  label: "Daily Brief",
  description: "Your country-aware first-run brief, in the order you selected.",
  preferredLength: "balanced" as const,
  targetMinutes: 5 as const,
  tone: "calm" as const,
  purpose: "daily_clarity" as const,
  narrationEnabled: false,
  sectionIdsForCountry: defaultBriefSequenceForCountry,
};

// Retain the historical wire id for already-saved profiles without offering
// the removed preset in the five-template picker.
const LEGACY_EXECUTIVE_STYLE: BriefStyleTemplate = {
  id: "investor",
  label: "Executive Brief",
  description: "Markets, watchlist, and the news that matters",
  preferredLength: "balanced",
  targetMinutes: 5,
  tone: "analytical",
  purpose: "executive",
  narrationEnabled: false,
  sectionIdsForCountry: executiveSections,
};

export const BRIEF_STYLE_TEMPLATES: BriefStyleTemplate[] = [
  {
    id: "quick_5",
    label: "Quick Daily Brief",
    description: "Markets, watchlist, and the news that matters",
    preferredLength: "quick",
    targetMinutes: 3,
    tone: "calm",
    purpose: "daily_clarity",
    narrationEnabled: false,
    sectionIdsForCountry: quickDailySections,
  },
  {
    id: "news_power",
    label: "Deep Focus Brief",
    description: "Markets, news, sport, planning, and learning",
    preferredLength: "deep",
    targetMinutes: 8,
    tone: "analytical",
    purpose: "deep_focus",
    narrationEnabled: false,
    premium: true,
    sectionIdsForCountry: deepFocusSections,
  },
  {
    id: "morning",
    label: "Daily Starter",
    description: "Weather, commute, tasks, My day, news, and quote",
    preferredLength: "balanced",
    targetMinutes: 5,
    tone: "coaching",
    purpose: "life_admin",
    narrationEnabled: false,
    sectionIdsForCountry: dailyStarterSections,
  },
  {
    id: "sports_fan",
    label: "Sports fan",
    description: "Scores and stories for your teams",
    preferredLength: "balanced",
    targetMinutes: 5,
    tone: "calm",
    purpose: "sports",
    narrationEnabled: false,
    sectionIdsForCountry: sportsFanSections,
  },
  {
    id: "learning",
    label: "Learning",
    description: "Ideas, facts, Daily Mastery, and today's idea",
    preferredLength: "deep",
    targetMinutes: 8,
    tone: "coaching",
    purpose: "learning",
    narrationEnabled: false,
    premium: true,
    sectionIdsForCountry: learningSections,
  },
];

export function briefStyleForId(
  styleId: BriefStyleId,
): BriefStyleTemplate | typeof DEFAULT_BRIEF_STYLE {
  if (styleId === "daily") return DEFAULT_BRIEF_STYLE;
  if (styleId === "investor") return LEGACY_EXECUTIVE_STYLE;
  return BRIEF_STYLE_TEMPLATES.find((style) => style.id === styleId) || DEFAULT_BRIEF_STYLE;
}

export function applyBriefStyleTemplate(
  profile: BriefProfile,
  template: BriefStyleTemplate,
  nowEpochMs = Date.now(),
): BriefProfile {
  const marketCountry = profile.marketCountry || "US";
  const sectionIds = template.sectionIdsForCountry(marketCountry);
  return {
    ...profile,
    name: template.label,
    depth: briefDepthForProfileLength(template.preferredLength),
    preferredLength: template.preferredLength,
    targetMinutes: briefAudioMinutesForLength(template.preferredLength),
    includeMarkets: sectionIds.some((id) => ["market_pulse", "watchlist"].includes(id)),
    includeTasks: sectionIds.includes("todos"),
    marketCountry,
    sectionIds,
    disabledSectionIds: [],
    sequenceVersion: DEFAULT_BRIEF_SEQUENCE_VERSION,
    styleId: template.id,
    templateId: template.id,
    purpose: template.purpose,
    tone: template.tone,
    timeOfDay: "morning",
    narrationEnabled: template.narrationEnabled,
    updatedAtEpochMs: nowEpochMs,
  };
}

/**
 * Mirrors Android's collection-wide country reconciliation. Every saved row
 * moves to the target market: custom rows retain their valid lineup,
 * ready-made rows are rehydrated from the target-market template, and a
 * template no longer offered by the catalog becomes a custom row. Only an
 * untouched active first-run profile is replaced by the new market default.
 */
export function migrateBriefProfileCollectionForCountryChange(
  collection: BriefProfileCollection,
  country: string,
  nowEpochMs = Date.now(),
): BriefProfileCollection {
  const marketCountry = normalizeCountry(country);
  const current = normalizeBriefProfileCollection(collection);
  const previousActive =
    current.profiles.find((profile) => profile.id === current.activeProfileId) ||
    current.profiles[0];
  const templatesById = new Map(BRIEF_STYLE_TEMPLATES.map((template) => [template.id, template]));
  const reconciledProfiles = current.profiles.map((profile) => {
    const templateId = profile.templateId.trim();
    if (!templateId) {
      return sanitizeBriefProfileForCountry(profile, marketCountry, nowEpochMs);
    }
    const template = templatesById.get(templateId as BriefStyleTemplate["id"]);
    if (template) {
      return applyBriefStyleTemplate({ ...profile, marketCountry }, template, nowEpochMs);
    }
    return sanitizeBriefProfileForCountry(
      {
        ...profile,
        templateId: "",
        updatedAtEpochMs: nowEpochMs,
      },
      marketCountry,
      nowEpochMs,
    );
  });
  const reconciledActive =
    reconciledProfiles.find((profile) => profile.id === previousActive.id) || reconciledProfiles[0];
  const nextActive = isUserCustomizedStyle(previousActive)
    ? reconciledActive
    : migrateBriefProfileForCountryChange(
        {
          ...reconciledActive,
          updatedAtEpochMs: nowEpochMs,
        },
        marketCountry,
      );

  return normalizeBriefProfileCollection(
    {
      profiles: reconciledProfiles.map((profile) =>
        profile.id === nextActive.id ? nextActive : profile,
      ),
      activeProfileId: nextActive.id,
    },
    marketCountry,
  );
}

function isReplaceableSeededDefault(profile: BriefProfile): boolean {
  const defaultSectionIds = defaultBriefSequenceForCountry(profile.marketCountry);
  return (
    !profile.templateId &&
    profile.name === "Daily Brief" &&
    profile.purpose === "daily_clarity" &&
    profile.preferredLength === "balanced" &&
    profile.tone === "calm" &&
    profile.timeOfDay === "morning" &&
    !profile.narrationEnabled &&
    profile.disabledSectionIds.length === 0 &&
    profile.sectionIds.length === defaultSectionIds.length &&
    profile.sectionIds.every((sectionId, index) => sectionId === defaultSectionIds[index])
  );
}

/**
 * Applies a ready-made style using Android's one-hidden-template-slot policy.
 * User-authored profiles are never overwritten; repeated template changes
 * rehydrate the same hidden ready-made row and make it active.
 */
export function applyBriefStyleTemplateToCollection(
  collection: BriefProfileCollection,
  template: BriefStyleTemplate,
  nowEpochMs = Date.now(),
): BriefProfileCollection {
  const current = normalizeBriefProfileCollection(collection);
  const active =
    current.profiles.find((profile) => profile.id === current.activeProfileId) ||
    current.profiles[0];
  const reusableReadyMade =
    active.templateId === template.id
      ? active
      : current.profiles.find((profile) => profile.templateId === template.id) ||
        (active.templateId ? active : undefined) ||
        current.profiles.find((profile) => Boolean(profile.templateId));
  const replaceableSeed = isReplaceableSeededDefault(active) ? active : undefined;
  const source = reusableReadyMade || replaceableSeed;
  if (!source && !canPersistReadyMadeBriefProfile(current)) return current;

  const target = applyBriefStyleTemplate(
    {
      ...(source || active),
      id: source?.id || newBriefProfileId(),
      marketCountry: active.marketCountry,
      createdAtEpochMs: source?.createdAtEpochMs || nowEpochMs,
    },
    template,
    nowEpochMs,
  );
  const targetExists = current.profiles.some((profile) => profile.id === target.id);
  const profiles = current.profiles.flatMap((profile) => {
    if (profile.id === target.id) return [target];
    return profile.templateId ? [] : [profile];
  });
  if (!targetExists) profiles.push(target);
  return normalizeBriefProfileCollection(
    {
      profiles,
      activeProfileId: target.id,
    },
    active.marketCountry,
  );
}

export function briefToneLabel(tone: BriefTone): string {
  switch (tone) {
    case "executive":
      return "Executive";
    case "coaching":
      return "Coaching";
    case "analytical":
      return "Analytical";
    case "calm":
    default:
      return "Calm";
  }
}

export function briefLengthLabel(length: BriefProfileLength): string {
  if (length === "quick") return "Quick";
  if (length === "deep") return "Deep";
  return "Standard";
}

export function briefProfileReadMinutes(profile: BriefProfile): BriefReadMinutes {
  return briefReadMinutesForLength(profile.preferredLength);
}

export function briefReadMinutesForSection(
  sectionId: string,
  length: BriefProfileLength | BriefDepth | BriefReadMinutes | BriefAudioMinutes,
): number {
  const id = String(sectionId || "")
    .trim()
    .toLowerCase();
  const deep = length === "deep" || length === 8;
  if (id === "spark" || id === "quote") return 1;
  if (id === "market_pulse" || id === "watchlist") return deep ? 2 : 1;
  if (id.startsWith("news_global_") || id === "world_affairs") return deep ? 2 : 1;
  if (id === "daily_mastery" || id === "today_idea") return deep ? 2 : 1;
  return deep ? 2 : 1;
}
